import { useMutation, useQueryClient } from 'react-query';
import { TOpenModalTiposDocumento } from '..'
import { useMemo } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import axiosClient from '../../../../../../lib/axios';
import { enqueueSnackbar } from 'notistack';
import { TTipoDocReceber } from '../../../../../../types/api/financeiro/tipos-doc-receber';
import { Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, FormControl, FormControlLabel, FormHelperText, FormLabel, Grid, InputLabel, MenuItem, Radio, RadioGroup, Select, TextField } from '@mui/material';

export default function DialogCriacaoEditTiposDocumentos({
    defaultValue,
    open,
    handleClose
}: DialogCriacaoEditTiposDocumentosProps) {

    const queryClient = useQueryClient();

    //Valores iniciais
    const isEdit = useMemo(() => Boolean(defaultValue), [defaultValue])
    const valoresIniciais = useMemo(() => defaultValue?.defaultValue ? defaultValue?.defaultValue : null, [defaultValue])
    //

    const methods = useForm<TTipoDocReceber>({
        defaultValues: {
            id: valoresIniciais?.id,
            nome: valoresIniciais?.nome,
            considera_bloqueio: valoresIniciais?.considera_bloqueio || 'N',
            dias_carencia_bloqueio: valoresIniciais?.dias_carencia_bloqueio,
            considera_limite: valoresIniciais?.considera_limite || 'Considera Sempre',
            tipo_limite_credito: valoresIniciais?.tipo_limite_credito || 'Geral',
            considera_fechamento_cco: valoresIniciais?.considera_fechamento_cco || "N"
        }
    })
    const { control, handleSubmit, setValue, getFieldState, watch, getValues } = methods;

    //Querys e mutations
    const { mutate: criarTipoDocumento, isLoading: isLoadingCriarTipos } = useMutation({
        mutationKey: 'cadastrar-tipo-documento',
        mutationFn: (inputs: any) => axiosClient.post("financeiro/configuracoes/cadastrar-tipo-documento", inputs),
        onError: (error) => {
            console.error(error)
            enqueueSnackbar('Erro ao cadastrar tipo documento!', { variant: 'error' });
        },
        onSuccess: () => {
            queryClient.invalidateQueries(["pesquisar-tipos-documento"])
            handleClose()
            enqueueSnackbar('Cadastro de tipo documento realizado com sucesso!', { variant: 'success' });
        }
    })

    const { mutate: attTipoDocumento, isLoading: isLoadingAttTipos } = useMutation({
        mutationKey: 'atualizar-tipo-documento',
        mutationFn: (inputs: any) => axiosClient.put(`financeiro/configuracoes/atualizar-tipo-documento/${valoresIniciais?.id}`, inputs),
        onError: (error) => {
            console.error(error)
            enqueueSnackbar('Erro ao atualizar tipo documento!', { variant: 'error' });
        },
        onSuccess: () => {
            queryClient.invalidateQueries(["pesquisar-tipos-documento"])
            handleClose()
            enqueueSnackbar('Atualização de tipo documento com sucesso!', { variant: 'success' });
        }
    })
    //

    //Funções manipuladoras
    const onSubmit = (data: TTipoDocReceber) => {
        const params = {
            nome: data.nome,
            codigo: data.id,
            considera_bloqueio: data.considera_bloqueio,
            dias_carencia_bloqueio: data.dias_carencia_bloqueio,
            considera_limite: data.considera_limite,
            tipo_limite_credito: data.tipo_limite_credito,
            considera_fechamento_cco: data.considera_fechamento_cco
        }
        if (isEdit) {
            attTipoDocumento(params)
        }
        if (!isEdit) {
            criarTipoDocumento(params)
        }
    }
    //

    return (
        <FormProvider {...methods}>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {`${!isEdit ? "Adicionar" : "Editar"} tipo documento`}
                </DialogTitle>

                {
                    (isLoadingAttTipos || isLoadingCriarTipos) &&
                    <Box display="flex" justifyContent="center" mt={4} mb={8}>
                        <CircularProgress />
                    </Box>
                }

                {
                    !isLoadingAttTipos && !isLoadingCriarTipos &&
                    <>
                        <Box
                            component={'form'}
                            onSubmit={handleSubmit(onSubmit)}
                        >
                            <DialogContent>
                                <Grid container spacing={2}>
                                    <Grid size={{ xs: 6 }}>
                                        <Controller
                                            name="id"
                                            control={control}
                                            rules={{
                                                required: { value: true, message: 'Campo obrigatório' },
                                                maxLength: { value: 3, message: 'Máximo de 3 caracteres' }
                                            }}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    defaultValue={valoresIniciais?.id}
                                                    label="Código"
                                                    required
                                                    variant="outlined"
                                                    fullWidth
                                                    error={getFieldState('id').invalid}
                                                    helperText={getFieldState('id').error?.message || ""}
                                                    slotProps={{ htmlInput: { maxLength: 3 } }}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 6 }}>
                                        <Controller
                                            name="nome"
                                            control={control}
                                            rules={{ required: 'Campo obrigatório' }}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    defaultValue={valoresIniciais?.nome}
                                                    label="Nome"
                                                    required
                                                    variant="outlined"
                                                    fullWidth
                                                    error={getFieldState('nome').invalid}
                                                    helperText={getFieldState('nome').error?.message || ""}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 6 }}>
                                        <Controller
                                            name="tipo_limite_credito"
                                            control={control}
                                            rules={{ required: 'Campo obrigatório' }}
                                            render={({ field }) => (
                                                <FormControl required fullWidth {...field} error={getFieldState('tipo_limite_credito').invalid}>
                                                    <InputLabel id="tipo-limite-credito-select">Tipo limite de crédito</InputLabel>
                                                    <Select
                                                        labelId="tipo-limite-credito-select"
                                                        defaultValue={valoresIniciais?.tipo_limite_credito}
                                                        value={getValues('tipo_limite_credito')}
                                                        label="Tipo limite de crédito*"
                                                        onChange={(e) => {
                                                            field.onChange(e.target.value)
                                                        }}
                                                    >
                                                        {
                                                            valoresTipoLimiteCredito.length > 0 && valoresTipoLimiteCredito.map((item, index) => <MenuItem key={index} value={item.value}>{item.value}</MenuItem>)
                                                        }
                                                    </Select>
                                                    {getFieldState('tipo_limite_credito').error && (
                                                        <FormHelperText>{getFieldState('tipo_limite_credito').error?.message}</FormHelperText>
                                                    )}
                                                </FormControl>
                                            )}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 6 }}>
                                        <Controller
                                            name="considera_limite"
                                            control={control}
                                            rules={{ required: 'Campo obrigatório' }}
                                            render={({ field }) => (
                                                <FormControl required fullWidth {...field} error={getFieldState('considera_limite').invalid}>
                                                    <InputLabel id="considera-limite-select">Considera limite de crédito?</InputLabel>
                                                    <Select
                                                        labelId="considera-limite-select"
                                                        defaultValue={valoresIniciais?.considera_limite}
                                                        value={getValues('considera_limite')}
                                                        label="Considera limite de crédito*"
                                                        onChange={(e) => {
                                                            field.onChange(e.target.value)
                                                        }}
                                                    >
                                                        {
                                                            valoresConsideraLimite.length > 0 && valoresConsideraLimite.map((item, index) => <MenuItem key={index} value={item.value}>{item.value}</MenuItem>)
                                                        }
                                                    </Select>
                                                    {getFieldState('considera_limite').error && (
                                                        <FormHelperText>{getFieldState('considera_limite').error?.message}</FormHelperText>
                                                    )}
                                                </FormControl>
                                            )}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 7 }}>
                                        <Controller
                                            name={'considera_bloqueio'}
                                            control={control}
                                            rules={{ required: 'Campo obrigatório' }}
                                            render={({ field }) => (
                                                <FormControl required fullWidth {...field} error={getFieldState('considera_bloqueio').invalid}>
                                                    <FormLabel id={`considera_bloqueio-radio`}>Considera bloqueio?</FormLabel>
                                                    <RadioGroup
                                                        row
                                                        aria-labelledby="considera_bloqueio-radio"
                                                        value={watch('considera_bloqueio')}
                                                        onChange={(e) => {
                                                            field.onChange(e.target.value);

                                                            setValue('dias_carencia_bloqueio', 0);

                                                        }}
                                                    >
                                                        {
                                                            valoresConsideraBloqueio.map((opcao, index) => (
                                                                <FormControlLabel key={index} value={opcao.value} control={<Radio />} label={opcao.label} />
                                                            ))
                                                        }
                                                    </RadioGroup>
                                                    {getFieldState('considera_bloqueio').error && (
                                                        <FormHelperText>{getFieldState('considera_bloqueio').error?.message}</FormHelperText>
                                                    )}
                                                </FormControl>
                                            )}
                                        />
                                    </Grid>
                                    {
                                        watch('considera_bloqueio') === 'S' &&
                                        <Grid size={{ xs: 5 }}>
                                            <Controller
                                                name="dias_carencia_bloqueio"
                                                control={control}
                                                rules={{ required: 'Campo obrigatório' }}
                                                render={({ field }) => (
                                                    <TextField
                                                        {...field}
                                                        defaultValue={valoresIniciais?.dias_carencia_bloqueio}
                                                        label="Dias carência bloqueio"
                                                        required
                                                        variant="outlined"
                                                        fullWidth
                                                        type='number'
                                                        error={getFieldState('dias_carencia_bloqueio').invalid}
                                                        helperText={getFieldState('dias_carencia_bloqueio').error?.message || ""}
                                                    />
                                                )}
                                            />
                                        </Grid>
                                    }
                                    <Grid size={{ xs: 7 }}>
                                        <Controller
                                            name={'considera_fechamento_cco'}
                                            control={control}
                                            rules={{ required: 'Campo obrigatório' }}
                                            render={({ field }) => (
                                                <FormControl required fullWidth {...field} error={getFieldState('considera_bloqueio').invalid}>
                                                    <FormLabel id={`considera_consumo-radio`}>Considera conta  consumo?</FormLabel>
                                                    <RadioGroup
                                                        row
                                                        aria-labelledby="considera_consumo-radio"
                                                        value={watch('considera_fechamento_cco')}
                                                        onChange={(e) => { field.onChange(e.target.value); }}
                                                    >
                                                        {
                                                            valoresConsideraBloqueio.map((opcao, index) => (
                                                                <FormControlLabel key={index} value={opcao.value} control={<Radio />} label={opcao.label} />
                                                            ))
                                                        }
                                                    </RadioGroup>
                                                    {getFieldState('considera_bloqueio').error && (
                                                        <FormHelperText>{getFieldState('considera_bloqueio').error?.message}</FormHelperText>
                                                    )}
                                                </FormControl>
                                            )}
                                        />
                                    </Grid>
                                </Grid>
                                <Box mt={2} width={'100%'} display={'flex'} justifyContent={'flex-end'} alignItems={'center'} gap={2}>
                                    <Button
                                        onClick={handleClose}
                                    >
                                        Cancelar
                                    </Button>
                                    <Button
                                        variant="contained"
                                        type="submit"
                                        autoFocus
                                        disabled={isLoadingCriarTipos || isLoadingAttTipos}

                                    >
                                        {
                                            (isLoadingCriarTipos || isLoadingAttTipos) &&
                                            <CircularProgress size={20} sx={{ marginRight: 1 }} />
                                        }
                                        {!isEdit ? "Adicionar" : "Atualizar"}
                                    </Button>
                                </Box>
                            </DialogContent>
                        </Box>
                    </>
                }
            </Dialog>
        </FormProvider>
    )
}

interface DialogCriacaoEditTiposDocumentosProps {
    defaultValue: TOpenModalTiposDocumento,
    open: boolean,
    handleClose: () => void
}

const valoresTipoLimiteCredito = [
    { value: 'Consumo' },
    { value: 'Financiamento' },
    { value: 'Sacaria' },
    { value: 'Veterinário' },
    { value: 'Fertilizantes' },
    { value: 'Serviços' },
    { value: 'Geral' }
]

const valoresConsideraLimite = [
    { value: 'Considera Sempre' },
    { value: 'Não Considera' },
    { value: 'Considera apenas se o vencimento está no período do limite' }
]

const valoresConsideraBloqueio = [
    { label: 'Considera', value: 'S' },
    { label: 'Não considera', value: 'N' },
]